import {
	useForm,
	FormProvider,
	getFormProps,
	getInputProps,
} from '@conform-to/react'
import { getZodConstraint, parseWithZod } from '@conform-to/zod'
import { Form } from '@remix-run/react'
import { ImagePlusIcon } from 'lucide-react'
import { useState, useCallback } from 'react'
import { useDropzone } from 'react-dropzone-esm'
import { z } from 'zod'
import { ErrorList } from '#app/components/forms'
import { Button } from '#app/components/ui/button'

const MAX_SIZE = 1024 * 1024 * 10 // 10MB

const TargetImageSchema = z.object({
	intent: z.literal('from-photo'),
	photoFile: z
		.instanceof(File)
		.refine(file => file.size > 0, 'Image is required')
		.refine(file => file.size <= MAX_SIZE, 'Image size must be less than 10MB'),
})

export const NewTargetForm = () => {
	const [photoForm, photoFields] = useForm({
		id: 'target-photo',
		constraint: getZodConstraint(TargetImageSchema),
		onValidate({ formData }) {
			return parseWithZod(formData, { schema: TargetImageSchema })
		},
		shouldRevalidate: 'onBlur',
	})

	const [newImageSrc, setNewImageSrc] = useState<string | null>(null)

	const onDrop = useCallback(
		(acceptedFiles: File[]) => {
			const file = acceptedFiles[0]
			if (file) {
				const reader = new FileReader()
				reader.onload = event => {
					setNewImageSrc(event.target?.result?.toString() ?? null)
				}
				reader.readAsDataURL(file)

				// Update the form field
				const dataTransfer = new DataTransfer()
				dataTransfer.items.add(file)
				const input = document.querySelector(
					`#${photoFields.photoFile.id}`,
				) as HTMLInputElement
				if (input) {
					input.files = dataTransfer.files
				}
			}
		},
		[photoFields.photoFile.id],
	)

	const {
		getRootProps,
		getInputProps: getDropzoneInputProps,
		isDragActive,
	} = useDropzone({
		onDrop,
		accept: { 'image/*': [] },
		maxSize: MAX_SIZE,
		multiple: false,
	})

	return (
		<FormProvider context={photoForm.context}>
			<Form
				method="POST"
				encType="multipart/form-data"
				className="flex flex-col items-center justify-center gap-4"
				onReset={() => setNewImageSrc(null)}
				{...getFormProps(photoForm)}
			>
				<div {...getRootProps()} className="cursor-pointer">
					{newImageSrc ? (
						<img
							src={newImageSrc}
							className="h-52 w-52 object-cover"
							alt="New target"
						/>
					) : (
						<div
							className={`flex w-full items-center justify-center rounded-md border-2 border-dashed hover:border-blue-300 ${isDragActive ? 'border-blue-500' : 'border-gray-300'} group p-4`}
						>
							<span className="flex items-center gap-4 text-gray-400 group-hover:text-blue-500">
								<ImagePlusIcon className="h-8 w-8" />
								{isDragActive
									? 'Drop the image here'
									: 'Drag & drop an image here or click to select file'}
							</span>
						</div>
					)}
					<input
						{...getInputProps(photoFields.photoFile, { type: 'file' })}
						{...getDropzoneInputProps()}
					/>
				</div>
				<ErrorList
					errors={photoFields.photoFile.errors}
					id={photoFields.photoFile.id}
				/>

				<p className="text-sm text-gray-500">
					Photo will be processed with computer vision intelligence to extract a
					detailed caption and scene graph for analysis.
				</p>
				<Button
					className="w-full"
					type="submit"
					name="intent"
					value={'from-photo'}
				>
					Submit Image
				</Button>
			</Form>
			<hr />
			<Form method="POST" encType="multipart/form-data">
				<Button
					className="w-full"
					type="submit"
					name="intent"
					value={'from-random'}
				>
					Submit Random
				</Button>
			</Form>
		</FormProvider>
	)
}
